/**
 * Portal capability model for managed-theater vs optional Workspace CRM.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { setDemoCrmUnlocked } from '../demo/demoApi.js';

export type PortalMode = 'managed' | 'crm';

export interface PortalCapabilities {
  mode: PortalMode;
  /** Full product theater visible */
  showTheater: true;
  /** Can mutate pipeline / lead disposition */
  canDispositionLeads: boolean;
  /** Can edit AI brain / DQ lists */
  canEditBrain: boolean;
  /** Can edit sequences */
  canEditSequences: boolean;
  /** Can override TCPA */
  canEditTcpa: boolean;
  /** Can toggle AI per lead */
  canToggleAiPerLead: boolean;
  /** Can send messages from inbox */
  canSendMessages: boolean;
  /** Account routing fields */
  canEditTransferPhone: boolean;
  canEditAvailability: boolean;
  label: string;
  subtitle: string;
}

function capsFor(mode: PortalMode): PortalCapabilities {
  if (mode === 'crm') {
    return {
      mode,
      showTheater: true,
      canDispositionLeads: true,
      canEditBrain: false,
      canEditSequences: false,
      canEditTcpa: false,
      canToggleAiPerLead: true,
      canSendMessages: false,
      canEditTransferPhone: true,
      canEditAvailability: true,
      label: 'Workspace CRM',
      subtitle: 'Ava still runs the factory. You can work leftovers in pipeline & leads.',
    };
  }
  return {
    mode,
    showTheater: true,
    canDispositionLeads: false,
    canEditBrain: false,
    canEditSequences: false,
    canEditTcpa: false,
    canToggleAiPerLead: false,
    canSendMessages: false,
    canEditTransferPhone: true,
    canEditAvailability: true,
    label: 'Managed by ContactRate',
    subtitle: 'Full visibility. Factory controls are view-only — Ava works your leads.',
  };
}

interface PortalContextValue {
  caps: PortalCapabilities;
  mode: PortalMode;
  setMode: (m: PortalMode) => void;
  toast: string | null;
  flash: (msg: string) => void;
  guardWrite: (allowed: boolean, blockedMsg: string) => boolean;
}

const PortalContext = createContext<PortalContextValue | null>(null);

const STORAGE_KEY = 'cr_demo_portal_mode';

export function PortalProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<PortalMode>(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      return v === 'crm' ? 'crm' : 'managed';
    } catch {
      return 'managed';
    }
  });
  const [toast, setToast] = useState<string | null>(null);

  const setMode = useCallback((m: PortalMode) => {
    setModeState(m);
    try {
      localStorage.setItem(STORAGE_KEY, m);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    setDemoCrmUnlocked(mode === 'crm');
  }, [mode]);

  const flash = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2800);
  }, []);

  const guardWrite = useCallback(
    (allowed: boolean, blockedMsg: string) => {
      if (allowed) return true;
      flash(blockedMsg);
      return false;
    },
    [flash],
  );

  const value = useMemo<PortalContextValue>(
    () => ({
      caps: capsFor(mode),
      mode,
      setMode,
      toast,
      flash,
      guardWrite,
    }),
    [mode, setMode, toast, flash, guardWrite],
  );

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
}

export function usePortal() {
  const ctx = useContext(PortalContext);
  if (!ctx) throw new Error('usePortal requires PortalProvider');
  return ctx;
}
